import axios from 'axios'
import apiConfig from './config.ts'
import { setMaintenanceMode } from '../utils/maintenance'

// 创建axios实例
const api = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: apiConfig.headers
})

// 创建专门用于文件上传的axios实例
const uploadApi = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.uploadTimeout, // 使用更长的超时时间
  headers: {
    'Content-Type': 'multipart/form-data'
  }
})

// 打印API配置（调试用）
apiConfig.printConfig();

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 优先使用pilot token，如果没有则使用普通token
    const pilotToken = localStorage.getItem('pilot_token')
    const token = localStorage.getItem('token')
    
    if (pilotToken) {
      config.headers.Authorization = `Bearer ${pilotToken}`
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 检测维护模式响应
    if (response.data?.maintenance === true) {
      setMaintenanceMode(true, response.data?.message || '系统正在维护中，请稍后再试')
      return Promise.reject(new Error(response.data?.message || '系统正在维护中'))
    }
    // 直接返回响应数据，而不是整个response对象
    return response.data
  },
  (error) => {
    // 检测503维护模式响应
    if (error.response?.status === 503 || error.response?.data?.maintenance === true) {
      setMaintenanceMode(true, error.response?.data?.message || '系统正在维护中，请稍后再试')
      return Promise.reject(error.response?.data || error)
    }

    if (error.response?.status === 401) {
      // token过期或无效，清除本地存储
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('pilot_token')
      localStorage.removeItem('pilot_user')
      localStorage.removeItem('pilot_token_expires')

      // 只有在非首页时才跳转到登录页
      if (window.location.pathname !== '/' && window.location.pathname !== '/home') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error.response?.data || error)
  }
)

// 为uploadApi添加相同的拦截器
uploadApi.interceptors.request.use(
  (config) => {
    // 优先使用pilot token，如果没有则使用普通token
    const pilotToken = localStorage.getItem('pilot_token')
    const token = localStorage.getItem('token')
    
    if (pilotToken) {
      config.headers.Authorization = `Bearer ${pilotToken}`
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

uploadApi.interceptors.response.use(
  (response) => {
    // 检测维护模式响应
    if (response.data?.maintenance === true) {
      setMaintenanceMode(true, response.data?.message || '系统正在维护中，请稍后再试')
      return Promise.reject(new Error(response.data?.message || '系统正在维护中'))
    }
    // 直接返回响应数据，而不是整个response对象
    return response.data
  },
  (error) => {
    // 检测503维护模式响应
    if (error.response?.status === 503 || error.response?.data?.maintenance === true) {
      setMaintenanceMode(true, error.response?.data?.message || '系统正在维护中，请稍后再试')
      return Promise.reject(error.response?.data || error)
    }

    if (error.response?.status === 401) {
      // token过期或无效，清除本地存储
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('pilot_token')
      localStorage.removeItem('pilot_user')
      localStorage.removeItem('pilot_token_expires')

      // 只有在非首页时才跳转到登录页
      if (window.location.pathname !== '/' && window.location.pathname !== '/home') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error.response?.data || error)
  }
)

// 认证相关API
export const authAPI = {
  // 用户注册
  register: (data: {
    username: string
    email: string
    password: string
    real_name?: string
  }) => api.post('/auth/register', data),

  // 用户登录
  login: (data: { username: string; password: string }) =>
    api.post('/auth/login', data),

  // 获取当前用户信息
  getMe: () => api.get('/auth/me'),

  // 更新用户信息
  updateProfile: (data: {
    real_name?: string
    phone?: string
    birth_date?: string
    gender?: string
    location?: string
    bio?: string
  }) => api.put('/auth/profile', data),

  // 修改密码
  changePassword: (data: {
    current_password: string
    new_password: string
  }) => api.put('/auth/password', data),

  // 用户登出
  logout: () => api.post('/auth/logout')
}

// 飞行员认证相关API
export const pilotAuthAPI = {
  // 飞行员登录
  login: (data: { callsign: string; password: string }) =>
    api.post('/pilot-auth/login', data),

  // 获取当前飞行员信息
  getMe: () => api.get('/pilot-auth/profile'),

  // 飞行员登出
  logout: () => api.post('/pilot-auth/logout'),

  // 验证token
  verify: () => api.get('/pilot-auth/verify'),

  // 刷新token
  refresh: () => api.post('/pilot-auth/refresh'),

  // 检查呼号是否存在
  checkCallsign: (callsign: string) => api.get(`/pilot-auth/check-callsign/${callsign}`),

  // 提交管制员申请
  submitApplication: (data: {
    qq: string
    discoveryChannel: string
    flightSimContact: string
    flightSimExperience: string
    eventCount: number
    hasAdvancedEvent: boolean
    flightSimSoftware: string[]
    otherSoftware?: string
    aircraftTypes: string
    otherAtcExperience?: string
    englishLevel: string
    age: number
    similarities: string
    motivation: string
    atcUnderstanding: string
    atcPilotRelation: string
    atcQualities: string
    agreement: boolean
    contactEmail?: string
  }) => api.post('/pilot-auth/application', data),

  // 确认提交管制员申请（字数较少时的确认提交）
  confirmApplication: (data: {
    qq: string
    discoveryChannel: string
    flightSimContact: string
    flightSimExperience: string
    eventCount: number
    hasAdvancedEvent: boolean
    flightSimSoftware: string[]
    otherSoftware?: string
    aircraftTypes: string
    otherAtcExperience?: string
    englishLevel: string
    age: number
    similarities: string
    motivation: string
    atcUnderstanding: string
    atcPilotRelation: string
    atcQualities: string
    agreement: boolean
    contactEmail?: string
    confirmed: boolean
  }) => api.post('/pilot-auth/application/confirm', data),

  // 获取申请状态
  getApplicationStatus: () => api.get('/pilot-auth/application/status'),

  // 审核相关API（仅教员可访问）
  // 获取待审核的申请列表
  getAdminApplications: () => api.get('/pilot-auth/admin/applications'),

  // 认领学员申请
  claimApplication: (applicationId: number) => api.post(`/pilot-auth/admin/applications/${applicationId}/claim`),

  // 获取申请详情（审核用）
  getAdminApplicationDetail: (applicationId: number) => api.get(`/pilot-auth/admin/applications/${applicationId}`),

  // 审核申请（审批通过/拒绝、面试通过/拒绝）
  decideApplication: (applicationId: number, data: { 
    decision: 'approval_pass' | 'approval_reject' | 'interview_pass' | 'interview_reject'; 
    comment?: string;
    rejection_reason?: string;
  }) => api.post(`/pilot-auth/admin/applications/${applicationId}/decide`, data),

  // 获取申请留言列表
  getApplicationMessages: (applicationId: number) => 
    api.get(`/pilot-auth/application/${applicationId}/messages`),

  // 发送留言
  sendMessage: (applicationId: number, data: { message: string }) => 
    api.post(`/pilot-auth/application/${applicationId}/messages`, data),

  // 标记留言为已读
  markMessagesAsRead: (applicationId: number) => 
    api.put(`/pilot-auth/application/${applicationId}/messages/read`),

  // 获取未读留言数量
  getUnreadMessageCount: () => 
    api.get('/pilot-auth/messages/unread-count'),

  // 重置所有未读消息
  resetUnreadMessages: () => 
    api.post('/pilot-auth/messages/reset-unread'),

  // 清除申请记录（重新申请）
  deleteApplication: () => 
    api.delete('/pilot-auth/application'),

  // 访问控制相关API（仅管理员可访问）
  // 获取访问控制设置
  getAccessControlSettings: () => 
    api.get('/pilot-auth/admin/access-control'),

  // 更新访问控制设置
  updateAccessControlSettings: (data: { access_control_enabled: boolean }) => 
    api.put('/pilot-auth/admin/access-control', data)
}

// 首页相关API
export const homeAPI = {
  // 获取首页数据
  getData: () => api.get('/home/data'),

  // 获取公告列表
  getAnnouncements: (params?: {
    page?: number
    limit?: number
    type?: string
    priority?: string
  }) => api.get('/home/announcements', { params }),

  // 获取活动列表
  getEvents: (params?: {
    page?: number
    limit?: number
    type?: string
    status?: string
  }) => api.get('/home/events', { params }),

  // 获取最新活动（首页用）
  getLatestEvents: () => api.get('/home/latest-events'),

  // 获取徽章变更日志
  getBadgeChangeLogs: () => api.get('/home/badge-change-logs'),

  // 获取排行榜
  getRankings: (params?: { type?: string; limit?: number }) =>
    api.get('/home/rankings', { params }),

  // 获取统计信息
  getStats: () => api.get('/home/stats'),

  // 获取最新注册
  getLatestRegistrations: () => api.get('/home/latest-registrations')
}

// 公告相关API
export { publicAnnouncementsAPI, adminAnnouncementsAPI } from './announcements'

// 用户相关API
export const userAPI = {
  // 获取用户列表
  getUsers: (params?: {
    page?: number
    limit?: number
    role?: string
    status?: string
  }) => api.get('/users', { params }),

  // 获取用户详情
  getUser: (id: number) => api.get(`/users/${id}`),

  // 更新用户状态
  updateUserStatus: (id: number, status: string) =>
    api.put(`/users/${id}/status`, { status })
}

// 权限管理相关API
export const permissionAPI = {
  // 获取权限列表
  getPermissions: (params?: {
    category?: string
    page?: number
    limit?: number
  }) => api.get('/permissions/list', { params }),

  // 获取用户权限
  getUserPermissions: (userId: number) => api.get(`/permissions/user/${userId}`),

  // 分配权限
  grantPermissions: (data: {
    user_id: number
    permission_ids: number[]
    expires_at?: string
    reason?: string
  }) => api.post('/permissions/grant', data),

  // 撤销权限
  revokePermissions: (data: {
    user_id: number
    permission_ids: number[]
    reason?: string
  }) => api.post('/permissions/revoke', data),

  // 获取当前用户权限
  getMyPermissions: () => api.get('/permissions/my-permissions'),

  // 获取权限统计
  getStats: () => api.get('/permissions/stats'),

  // 获取用户列表（用于权限管理）
  getUsers: (params?: {
    page?: number
    limit?: number
    search?: string
  }) => api.get('/permissions/users', { params })
}

// 活动相关API
export const eventAPI = {
  // 获取活动详情
  getEvent: (id: number) => api.get(`/events/${id}`),

  // 参加活动
  joinEvent: (id: number) => api.post(`/events/${id}/join`),

  // 退出活动
  leaveEvent: (id: number) => api.post(`/events/${id}/leave`),

  // 创建活动
  createEvent: (data: {
    title: string
    description: string
    type: string
    start_time: string
    end_time: string
    location?: string
    max_participants?: number
    requirements?: any
  }) => api.post('/events', data),

  // 更新活动
  updateEvent: (id: number, data: any) => api.put(`/events/${id}`, data),

  // 删除活动
  deleteEvent: (id: number) => api.delete(`/events/${id}`),

  // 获取活动评价
  getEventReviews: (id: number) => api.get(`/events/${id}/reviews`),

  // 提交活动评价
  submitEventReview: (id: number, data: { rating: number; comment?: string; is_anonymous?: boolean }) => 
    api.post(`/events/${id}/reviews`, data),

  // 检查用户是否已评价
  checkUserReview: (id: number) => api.get(`/events/${id}/reviews/check`),

  // 领取活动奖励积分
  claimEventReward: (eventId: number) => api.post(`/events/claim/${eventId}`),

  // 获取可领取积分的活动列表
  getClaimableEvents: () => api.get('/events/claimable'),

  // 获取活动状态汇总（报名/完成/领取）
  getEventStatusSummary: () => api.get('/events/status/summary')
}

// 职业生涯相关API
export const careerAPI = {
  // 获取职业概览
  getOverview: () => api.get('/career/overview'),
  // 获取值班记录
  getDutyRecords: (params?: {
    page?: number
    limit?: number
    duty_type?: string
    status?: string
  }) => api.get('/career/duty-records', { params }),
  // 创建值班记录
  createDutyRecord: (data: {
    duty_type: string
    start_time: string
    end_time?: string
    airport?: string
    notes?: string
  }) => api.post('/career/duty-records', data),
  // 获取管制记录
  getControlRecords: (params?: {
    page?: number
    limit?: number
    operation_type?: string
  }) => api.get('/career/control-records', { params }),
  // 创建管制记录
  createControlRecord: (data: {
    duty_record_id?: number
    aircraft_callsign?: string
    aircraft_type?: string
    operation_type: string
    runway?: string
    time: string
    weather_conditions?: any
    traffic_volume?: number
    incidents?: string
  }) => api.post('/career/control-records', data),
  // 获取培训记录
  getTrainingRecords: (params?: {
    page?: number
    limit?: number
    training_type?: string
    status?: string
  }) => api.get('/career/training-records', { params }),
  
  // ================ 处罚管理相关API ================
  
  // 获取处罚记录
  getPenalties: (params?: {
    page?: number
    limit?: number
    pilotcid?: string
    status?: string
    start_date?: string
    end_date?: string
  }) => api.get('/career/penalties', { params }),
  
  // 获取管制记录（当前管制员的）
  getControllerRecords: (params?: {
    page?: number
    limit?: number
  }) => api.get('/career/controller-records', { params }),
  
  // 获取违规规则（只取扣分规则）
  getViolationRules: () => api.get('/career/violation-rules'),
  
  // 提交处罚
  submitPenalty: (data: FormData) =>
    api.post('/career/penalty', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
  
  // 上传证据文件到Minio
  uploadEvidenceFile: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/career/upload-evidence', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  
  // 获取飞行员申诉记录
  getAppeals: (params?: {
    page?: number
    limit?: number
  }) => api.get('/career/appeals', { params }),
  
  // 处理申诉
  processAppeal: (data: {
    appeal_id: number
    appeal_status: string
    process_notes: string
    final_result?: string
  }) => api.post('/career/process-appeal', data),
  
  revokeAppeal: (data: {
    record_id: number
  }) => api.post('/career/revoke-appeal', data),

  // 获取机场详细记录
  getAirportRecords: (airportCode: string, params?: {
    page?: number
    page_size?: number
  }) => api.get(`/career/airport/${airportCode}/records`, { params })
}

// 空管学院相关API
export const academyAPI = {
  // 获取考试列表
  getExams: (params?: {
    page?: number
    limit?: number
    status?: string
  }) => api.get('/academy/exams', { params }),

  // 获取考试详情
  getExam: (id: number) => api.get(`/academy/exams/${id}`),

  // 报名考试
  registerExam: (id: number) => api.post(`/academy/exams/${id}/register`),

  // 获取考核记录
  getExamRecords: (params?: {
    page?: number
    limit?: number
    status?: string
  }) => api.get('/academy/exam-records', { params }),

  // 获取教程规定
  getTutorials: () => api.get('/academy/tutorials'),

  // 获取软件下载
  getSoftware: () => api.get('/academy/software'),

  // 获取绩效评估
  getPerformance: () => api.get('/academy/performance')
}

// 管理员相关API
export const adminAPI = {
  // 获取系统统计
  getSystemStats: () => api.get('/admin/stats'),

  // 获取用户管理数据
  getUserManagement: (params?: {
    page?: number
    limit?: number
    role?: string
    status?: string
  }) => api.get('/admin/users', { params }),

  // 更新用户角色
  updateUserRole: (id: number, role: string) =>
    api.put(`/admin/users/${id}/role`, { role }),

  // 获取系统日志
  getSystemLogs: (params?: {
    page?: number
    limit?: number
    action?: string
    user_id?: number
  }) => api.get('/admin/logs', { params })
}

export { api, uploadApi };
export default api 